/**
 * strings.js — centralized UI copy for Event Pre-Orders.
 *
 * English-only for v1, but kept in one flat object (and accessed via `t`) so a
 * Telugu bundle can be layered on later using the existing src/i18n approach
 * without touching component JSX.
 */
export const STRINGS_EN = {
  // Public form
  loading: 'Loading…',
  notFoundTitle: 'Event not found',
  notFoundBody: 'This pre-order link is invalid or has been removed.',
  notOpenTitle: 'Ordering not open yet',
  notOpenBody: 'Pre-orders for this event haven’t opened. Please check back soon.',
  closedTitle: 'Ordering is closed',
  closedBody: 'Thanks for your interest — pre-orders for this event are now closed.',
  deadlineTitle: 'Ordering deadline passed',
  deadlineBody: 'The window to pre-order for this event has ended.',
  alreadyOrderedTitle: 'You’ve already pre-ordered',
  alreadyOrderedBody: 'Here’s your order — you can update it any time before ordering closes.',
  thankYouTitle: 'Order received! 🎉',
  thankYouBody: 'Thanks — your pre-order has been submitted.',
  orderUpdatedTitle: 'Order updated! ✅',
  orderUpdatedBody: 'Your pre-order has been updated.',
  orderAgain: 'Place another order',
  editMyOrder: 'Edit my order',
  editYourOrder: 'Edit your order',
  updateOrder: 'Update my order',
  cancelEdit: 'Cancel',

  // Cross-device "find my order" lookup
  findMyOrder: 'Already ordered? Find your order',
  findMyOrderTitle: 'Find your order',
  findMyOrderBody: 'Enter the phone number you ordered with to view or edit your pre-order — from any device.',
  lookupPhoneLabel: 'Phone number',
  lookupFind: 'Find my order',
  lookupSearching: 'Searching…',
  lookupNotFound: 'No pre-order found for that phone number. Check the number, or place a new order below.',
  lookupNoPhone: 'Please enter the phone number you ordered with.',
  lookupUnavailable: 'Order lookup isn’t available right now. Please try again later or ask staff.',
  lookupClose: 'Close',

  // "You already ordered" screen (same phone already has an order)
  dupTitle: 'You’ve already ordered 👋',
  dupBody: 'We found an order under this phone number. You can add your new items to it, edit your existing order, or replace it.',
  dupExistingLabel: 'Your current order',
  dupAddingLabel: 'Adding now',
  dupAdd: '➕ Add these to my order',
  dupEditExisting: '✎ Edit my existing order',
  dupReplace: '↻ Replace with this new order',
  dupBack: '← Go back',
  orderAddedTitle: 'Added to your order! ➕',
  orderAddedBody: 'Your items were added to your existing order.',

  yourDetails: 'Your details',
  name: 'Name',
  phone: 'Phone',
  email: 'Email',
  chooseItems: 'Choose your items',
  viewMenu: 'View the menu',
  yourOrder: 'Your order',
  yourOrderQ: 'What would you like to order?',
  yourOrderPlaceholder: 'Type the items you’d like…',
  specialInstructions: 'Special instructions',
  instructionsPlaceholder: 'Allergies, preferences, anything else…',
  submit: 'Submit pre-order',
  submitting: 'Submitting…',
  itemsSelected: 'selected',
  subtotal: 'Subtotal',
  deadlineLabel: 'Order by',
  poweredBy: 'Desi Bites · Event Pre-Orders',

  // Admin — dashboard
  dashboardTitle: 'Event Pre-Orders',
  dashboardSubtitle: 'Create a shareable pre-order form for each event.',
  newEvent: 'New Event',
  noEvents: 'No events yet. Create your first pre-order form.',
  tabActive: 'Active',
  tabHistory: 'History',
  tabMenus: 'Menus',
  tabActivity: 'Activity',
  filterAll: 'All',
  filterEvents: 'Events',
  filterOrders: 'Orders',
  allEvents: 'All events',
  searchActivity: 'Search activity…',
  noActivity: 'No activity yet',
  orderHistoryLabel: 'History',
  hide: 'Hide',
  searchPlaceholder: 'Search events…',
  reopen: 'Reopen',
  restore: 'Restore',
  noHistory: 'No past events yet. Closed and archived events land here.',
  noSearch: 'No events match your search.',
  statEvents: 'Events',
  statOrders: 'Orders',
  statOpen: 'Open now',
  // Menu library
  menusSubtitle: 'Build a menu once, then reuse and edit it across events.',
  newMenu: 'New menu',
  editMenu: 'Edit menu',
  saveMenu: 'Save menu',
  menuName: 'Menu name',
  menuNamePlaceholder: 'e.g. Diwali Special Menu',
  noMenus: 'No saved menus yet.',
  menuSaved: 'Menu saved',
  loadMenu: 'Load a saved menu',
  load: 'Load',
  saveAsMenu: 'Save as menu',
  // Activity / revenue
  activity: 'Activity',
  revenue: 'Est. revenue',
  orders: 'orders',
  items: 'items',
  share: 'Share',
  preview: 'Preview',
  livePreview: 'Live preview',
  eventOpened: 'Event opened — link is live 🎉',
  eventClosed: 'Ordering closed',
  open: 'Open',
  close: 'Close',
  edit: 'Edit',
  responses: 'Responses',
  duplicate: 'Duplicate',
  del: 'Delete',
  archive: 'Archive',
  confirmDelete: 'Delete this event? Its pre-order link will stop working. This cannot be undone.',

  // Admin — builder
  newEventTitle: 'New Event',
  editEventTitle: 'Edit Event',
  basics: 'Event details',
  eventTitleLabel: 'Event title',
  eventTitlePlaceholder: 'e.g. Diwali Team Lunch',
  descriptionLabel: 'Description',
  descriptionPlaceholder: 'Tell people what this is about…',
  accentColor: 'Accent color',
  deadlineField: 'Ordering deadline (optional)',
  menuItemsTitle: 'Menu',
  menuItemsHint: 'Add each item below, or just paste a menu link — either works.',
  menuLinkLabel: 'Menu link (optional)',
  menuLinkPlaceholder: 'https://link-to-your-menu…',
  menuLinkHint: 'Link to a full menu (website, PDF, or image). Attendees can view it and type their order — handy when you don’t want to list every item.',
  formFieldsTitle: 'Custom questions',
  formFieldsHint: 'Extra questions attendees answer (dept, table, dietary…).',
  addField: 'Add question',
  settingsTitle: 'Settings',
  save: 'Save',
  saveAndOpen: 'Save & Open',
  cancel: 'Cancel',
  back: 'Back',

  // Settings toggles
  requireName: 'Require name',
  requirePhone: 'Require phone number',
  requireEmail: 'Require email',
  oneOrderPerDevice: 'One order per device',
  allowInstructions: 'Allow special instructions',
  showPrices: 'Show prices',
  payNoteLabel: 'Payment note',
  payNotePlaceholder: 'e.g. Free for staff / Pay at counter',
  capacityLabel: 'Order capacity (optional)',

  // Admin — responses
  kitchenSummary: 'Kitchen Summary',
  totalOrders: 'Total orders',
  totalItems: 'Total items',
  exportCsv: 'Export CSV',
  noResponses: 'No orders yet. Share the link to start collecting pre-orders.',

  // Admin — edit a response
  editOrderTitle: 'Edit order',
  editOrderSubtitle: 'Changes are saved to this attendee’s pre-order and logged in its history.',
  orderItems: 'Items',
  saveChanges: 'Save changes',
  saving: 'Saving…',
  orderSaveError: 'Couldn’t save the order. Please try again.',
  editedByAdmin: 'edited by staff',

  // Kitchen Summary / prep list
  sortLabel: 'Sort',
  sortAZ: 'A–Z',
  sortBusiest: 'Busiest',
  prepTypes: 'types',
  prepTotalRow: 'Total',
  copyPrep: '📋 Copy prep list',
  copiedPrep: '✓ Copied',
  prepParsedNote: 'Auto-counted from free-text orders — double-check quantities.',
  ordersAsTyped: '🍽 Orders (as typed)',
  noName: '(no name)',
};

/** Tiny translator — swap the bundle here when Telugu lands. */
export function t(key) {
  return STRINGS_EN[key] ?? key;
}
